/* IMPORT */
import { OWNER } from '../objects/superroot.js';
import { lazySetAdd, lazySetDelete } from '../lazy.js';
import cleanup from './cleanup.js';
import get from './get.js';
import memo from './memo.js';
import resolve from './resolve.js';
import suspense from './suspense.js';
import { frozen, readable } from '../objects/callable.js';
import Observable from '../objects/observable.js';
import Root from '../objects/root.js';
import { SYMBOL_CACHED, SYMBOL_SUSPENSE, SYMBOL_UNCACHED } from '../symbols.js';
import { callStack } from './debugger.js';
/* HELPERS */
const DUMMY_INDEX = frozen(-1);
class MappedRoot extends Root {
}
/* MAIN */
//TODO: Optimize this more
class CacheUnkeyed {
    /* CONSTRUCTOR */
    constructor(fn, pooled) {
        /* VARIABLES */
        this.parent = OWNER;
        this.suspense = OWNER.get(SYMBOL_SUSPENSE);
        this.cache = new Map();
        this.pool = [];
        this.poolMaxSize = 0;
        /* API */
        this.cleanup = () => {
            let pooled = 0;
            let poolable = Math.max(0, this.pooled ? this.poolMaxSize - this.pool.length : 0);
            // const stack = callStack()
            this.cache.forEach(mapped => {
                if (poolable > 0 && pooled++ < poolable) {
                    mapped.suspended?.set(true);
                    this.pool.push(mapped);
                }
                else {
                    mapped.dispose(true);
                }
            });
        };
        this.dispose = () => {
            if (this.suspense) {
                lazySetDelete(this.parent, 'roots', this.roots);
            }
            this.cache.forEach(mapped => {
                mapped.dispose(true);
            });
            this.pool.forEach(mapped => {
                mapped.dispose(true);
            });
        };
        this.map = (values) => {
            const { cache, fn, fnWithIndex } = this;
            const cacheNext = new Map();
            const results = new Array(values.length);
            const pool = this.pool;
            const pooled = this.pooled;
            let resultsCached = true; // Whether all results are cached, if so this enables an optimization
            let resultsUncached = true; // Whether all results are anew, if so this enables an optimization in Voby
            let leftovers = [];
            const stack = callStack();
            if (cache.size) {
                for (let i = 0, l = values.length; i < l; i++) {
                    const value = values[i];
                    const cached = cache.get(value);
                    if (cached) {
                        resultsUncached = false;
                        cache.delete(value);
                        cacheNext.set(value, cached);
                        cached.index?.set(i);
                        results[i] = cached.result; //TSC
                    }
                    else {
                        leftovers.push(i);
                    }
                }
            }
            else {
                leftovers = new Array(results.length);
            }
            outer: for (let i = 0, l = leftovers.length; i < l; i++) {
                const index = leftovers[i] || i;
                const value = values[index];
                const isDuplicate = cacheNext.has(value);
                if (!isDuplicate) {
                    for (const [key, mapped] of cache.entries()) {
                        cache.delete(key);
                        cacheNext.set(value, mapped);
                        mapped.index?.set(index);
                        mapped.value?.set(value);
                        results[index] = mapped.result; //TSC
                        continue outer;
                    }
                }
                resultsCached = false;
                let mapped;
                if (pooled && pool.length) {
                    mapped = pool.pop(); //TSC
                    mapped.index?.set(index);
                    mapped.value?.set(value);
                    mapped.suspended?.set(false);
                    results[index] = mapped.result; //TSC
                }
                else {
                    mapped = new MappedRoot(false);
                    mapped.wrap(() => {
                        let $index = DUMMY_INDEX;
                        if (fnWithIndex) {
                            mapped.index = new Observable(index);
                            $index = readable(mapped.index, stack);
                        }
                        const observable = mapped.value = new Observable(value);
                        const suspended = pooled ? new Observable(false) : undefined;
                        if (suspended)
                            suspended.stack = stack;
                        const $value = memo(() => get(observable.get())); //TSC
                        const result = results[index] = suspended ? suspense(() => suspended.get(), () => resolve(fn($value, $index)), stack) : resolve(fn($value, $index));
                        mapped.value = observable;
                        mapped.result = result;
                        mapped.suspended = suspended;
                    }, undefined, undefined, stack);
                }
                if (isDuplicate) { // Expensive, not reusable
                    cleanup(() => mapped.dispose(true));
                }
                else { // Cheap, reusable
                    cacheNext.set(value, mapped);
                }
            }
            this.poolMaxSize = Math.max(this.poolMaxSize, results.length);
            this.cleanup();
            this.cache = cacheNext;
            if (resultsCached) {
                results[SYMBOL_CACHED] = true;
            }
            if (resultsUncached) {
                results[SYMBOL_UNCACHED] = true;
            }
            return results;
        };
        this.roots = () => {
            return [...this.cache.values(), ...this.pool.values()];
        };
        this.fn = fn;
        this.fnWithIndex = (fn.length > 1);
        this.pooled = pooled;
        if (this.suspense) {
            lazySetAdd(this.parent, 'roots', this.roots);
        }
    }
}
/* EXPORT */
export default CacheUnkeyed;
